import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { CalendarDays, Users, CheckCircle2, UtensilsCrossed } from "lucide-react";
import api from "../utils/api";

const todayIST = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

export default function PublicBookingPage() {
  const { slug } = useParams();
  const [restaurant, setRestaurant] = useState(null);
  const [form, setForm] = useState({
    customerName: "", customerMobile: "", partySize: 2,
    date: todayIST(), time: "19:00", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/api/public/restaurant/${slug}`)
      .then(res => setRestaurant(res.data?.data || res.data))
      .catch(() => {});
  }, [slug]);

  const restaurantName = restaurant?.name || restaurant?.shop_type || "Restaurant";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.customerName.trim() || !/^\d{10}$/.test(form.customerMobile.trim())) {
      setError("Please enter your name and a valid 10-digit mobile number.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/api/public/restaurant/${slug}/reservation`, {
        ...form,
        partySize: Number(form.partySize) || 1,
      });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.message || "Booking failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-teal-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-sm w-full text-center">
          <CheckCircle2 size={48} className="mx-auto text-emerald-500 mb-4" />
          <h1 className="text-lg font-black text-slate-800 mb-1">Request received!</h1>
          <p className="text-sm text-slate-500 leading-relaxed">
            {restaurantName} will confirm your table for{" "}
            <strong>{form.partySize} guests</strong> on <strong>{form.date}</strong> at{" "}
            <strong>{form.time}</strong>. You'll get a call on {form.customerMobile} if anything changes.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-teal-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-indigo-500 px-6 py-6 text-center">
          <div className="w-12 h-12 mx-auto rounded-2xl bg-white/15 border border-white/25 flex items-center justify-center mb-2">
            <UtensilsCrossed size={22} className="text-white" />
          </div>
          <h1 className="text-lg font-black text-white">{restaurantName}</h1>
          <p className="text-[11px] text-indigo-100 mt-0.5">Reserve your table</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Your name</label>
            <input
              value={form.customerName}
              onChange={e => setForm(f => ({ ...f, customerName: e.target.value }))}
              placeholder="Full name"
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Mobile number</label>
            <input
              value={form.customerMobile}
              onChange={e => setForm(f => ({ ...f, customerMobile: e.target.value.replace(/\D/g, "").slice(0, 10) }))}
              placeholder="10-digit mobile"
              inputMode="numeric"
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1 flex items-center gap-1"><Users size={11} /> Guests</label>
              <input
                type="number" min="1" max="50"
                value={form.partySize}
                onChange={e => setForm(f => ({ ...f, partySize: e.target.value }))}
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1 flex items-center gap-1"><CalendarDays size={11} /> Date</label>
              <input
                type="date" min={todayIST()}
                value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                className="w-full border border-slate-200 rounded-xl px-2 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Time</label>
              <input
                type="time"
                value={form.time}
                onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
                className="w-full border border-slate-200 rounded-xl px-2 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Special requests (optional)</label>
            <input
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Birthday, window seat, high chair…"
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {error && (
            <p className="text-xs font-semibold text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{error}</p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded-xl transition-colors disabled:opacity-50"
          >
            {submitting ? "Booking…" : "Request Booking"}
          </button>
          <p className="text-[10px] text-slate-400 text-center">
            The restaurant will confirm your booking by phone.
          </p>
        </form>
      </div>
    </div>
  );
}
